import React, { useCallback, useEffect, useState } from 'react';
import { Play, Trash2, RotateCcw, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

// Gallery media can be gated (pending posts of a not-yet-approved DJ). A plain
// <img>/<video src> sends no Authorization header, so blob-fetch with the Bearer
// token (admin is authorized) and hand back an object URL, revoked on unmount.
function AuthMedia({ src, type, className }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true; let revoked = null;
    setUrl(null); setFailed(false);
    if (!src) { setFailed(true); return; }
    api.fetchAuthedBlobUrl(src)
      .then((u) => { if (alive) { revoked = u; setUrl(u); } else { URL.revokeObjectURL(u); } })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [src]);
  if (failed) return <div className={`${className} flex items-center justify-center bg-muted text-[10px] opacity-50`}>n/a</div>;
  if (!url) return <div className={`${className} flex items-center justify-center bg-muted text-[10px] opacity-50`}>…</div>;
  if (type === 'video') {
    return (
      <div className={`relative ${className}`}>
        <video src={url} className="w-full h-full object-cover" muted playsInline />
        <span className="absolute inset-0 flex items-center justify-center"><Play className="h-6 w-6 text-white drop-shadow" /></span>
      </div>
    );
  }
  return <img src={url} alt="" className={className} />;
}

const STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'border-amber-500 text-amber-600' },
  published: { label: 'Published', cls: 'bg-green-600' },
  admin_removed: { label: 'Removed by admin', cls: 'bg-red-600' },
};

/**
 * Admin moderation for one DJ's gallery, embedded in the /admin/djs detail panel.
 * Fetches GET /api/admin/djs/{djId}/gallery and soft-removes / restores posts.
 * Admin can preview here BEFORE approving the DJ.
 */
export default function AdminDJGallery({ djId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPosts(await api.get(`/admin/djs/${djId}/gallery`)); }
    catch (e) { toast.error(e.message || 'Failed to load gallery'); }
    finally { setLoading(false); }
  }, [djId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (post) => {
    const reason = window.prompt('Private removal note (optional, admin-only):', '');
    if (reason === null) return;   // cancelled
    setBusyId(post.id);
    try {
      await api.post(`/admin/djs/gallery/${post.id}/remove`, { delete_reason: reason.trim() || null });
      toast.success('Post hidden from public (kept for audit)');
      await load();
    } catch (e) { toast.error(e.message || 'Remove failed'); }
    finally { setBusyId(null); }
  };

  const restore = async (post) => {
    setBusyId(post.id);
    try {
      await api.post(`/admin/djs/gallery/${post.id}/restore`, {});
      toast.success('Post restored');
      await load();
    } catch (e) { toast.error(e.message || 'Restore failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="rounded-md border border-border/60 p-3">
      <div className="flex items-center justify-between mb-2">
        <b className="flex items-center gap-1"><Images className="h-4 w-4" /> Gallery posts</b>
        <span className="text-xs opacity-60">{posts.length} post{posts.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <p className="text-xs opacity-60 py-2">Loading gallery…</p>
      ) : posts.length === 0 ? (
        <p className="text-xs opacity-60 py-2">No gallery posts submitted.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => {
            const sb = STATUS_BADGE[p.status] || { label: p.status, cls: 'border' };
            const removed = p.status === 'admin_removed';
            const media = [...(p.media || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
            return (
              <div key={p.id} className={`rounded-md border p-2 ${removed ? 'border-red-500/40 bg-red-500/5' : 'border-border/50'}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    {p.event_name && <div className="text-sm font-medium truncate">{p.event_name}</div>}
                    {p.caption && <div className="text-xs opacity-70 truncate">{p.caption}</div>}
                    {!p.event_name && !p.caption && <div className="text-xs opacity-50">(no caption / event)</div>}
                  </div>
                  <Badge className={sb.cls} variant={removed || p.status === 'published' ? undefined : 'outline'}>{sb.label}</Badge>
                </div>

                <div className="flex flex-wrap gap-2">
                  {media.map((m, i) => (
                    <AuthMedia key={m.media_id || i} src={m.url} type={m.type}
                      className="h-16 w-16 rounded overflow-hidden border border-border/60" />
                  ))}
                </div>

                {removed && p.delete_reason && (
                  <p className="mt-2 text-[11px] text-red-600">Private note: {p.delete_reason}</p>
                )}

                <div className="mt-2 flex gap-2">
                  {removed ? (
                    <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => restore(p)}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="destructive" disabled={busyId === p.id} onClick={() => remove(p)}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove post
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-2 text-[11px] opacity-50">Removing hides a post publicly and records a private note; files are kept for audit. You can still approve the DJ.</p>
    </div>
  );
}
